import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, UserButton } from "@clerk/clerk-react";
import Header from "../components/Header.jsx";

const HAS_CLERK = Boolean(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY);

export default function AdminPage() {
  const nav = useNavigate();
  const auth = HAS_CLERK ? useAuth() : null;

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("");
  const [sort, setSort] = useState("newest");

  // Bounce to sign-in if Clerk's loaded and user isn't signed in
  useEffect(() => {
    if (!HAS_CLERK) return;
    if (auth && auth.isLoaded && !auth.isSignedIn) {
      nav("/admin/login");
    }
  }, [auth?.isLoaded, auth?.isSignedIn, nav]);

  useEffect(() => {
    if (HAS_CLERK && auth && (!auth.isLoaded || !auth.isSignedIn)) return;

    const fetchReviews = async () => {
      try {
        const headers = {};
        if (HAS_CLERK && auth) {
          const token = await auth.getToken();
          if (token) headers.Authorization = `Bearer ${token}`;
        }
        const r = await fetch("/api/admin/reviews", { headers, credentials: "include" });
        if (r.status === 401) {
          nav("/admin/login");
          return;
        }
        const d = await r.json();
        setReviews(d.reviews || []);
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, [auth?.isLoaded, auth?.isSignedIn, nav]);

  const stats = useMemo(() => {
    const m = { all: reviews.length, published: 0, pending: 0, hidden: 0 };
    for (const r of reviews) m[r.status] = (m[r.status] || 0) + 1;
    return m;
  }, [reviews]);

  const allTags = useMemo(() => {
    const m = new Set();
    reviews.forEach((r) => (r.tags || []).forEach((t) => m.add(t)));
    return [...m].sort();
  }, [reviews]);

  const filtered = useMemo(() => {
    let out = reviews;
    if (statusFilter !== "all") out = out.filter((r) => r.status === statusFilter);
    if (tagFilter) out = out.filter((r) => r.tags?.includes(tagFilter));
    if (query) {
      const q = query.toLowerCase();
      out = out.filter((r) =>
        `${r.text || ""} ${r.title || ""} ${r.customer?.name || ""} ${r.customer?.company || ""} ${r.customer?.email || ""}`
          .toLowerCase()
          .includes(q)
      );
    }
    if (sort === "newest") {
      out = [...out].sort(
        (a, b) =>
          new Date(b.dates?.review || 0).getTime() -
          new Date(a.dates?.review || 0).getTime()
      );
    } else if (sort === "rating") {
      out = [...out].sort((a, b) => (b.rating || 0) - (a.rating || 0));
    } else if (sort === "name") {
      out = [...out].sort((a, b) =>
        (a.customer?.name || "").localeCompare(b.customer?.name || "")
      );
    }
    return out;
  }, [reviews, query, statusFilter, tagFilter, sort]);

  const exportCsv = () => {
    const rows = [
      ["id", "status", "rating", "name", "email", "company", "title", "tags", "review_date", "text"],
      ...filtered.map((r) => [
        r.id, r.status, r.rating ?? "", r.customer?.name ?? "", r.customer?.email ?? "",
        r.customer?.company ?? "", r.customer?.title ?? "", (r.tags || []).join("|"),
        r.dates?.review ?? "", (r.text || "").replace(/\r?\n/g, " ").replace(/"/g, '""'),
      ]),
    ];
    const csv = rows.map((row) => row.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `reviews-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-10">
        <div className="flex items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-3xl font-bold">Admin · Reviews</h1>
            <p className="text-sm text-white/60 mt-1">
              Master view of every review — published, pending, and hidden.
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <button onClick={exportCsv} className="px-4 py-2 rounded-lg border border-white/15 hover:border-white/35 transition text-sm">
              Export CSV
            </button>
            <a href="https://app.senja.io/" target="_blank" rel="noopener" className="btn-flame text-sm">
              Open Senja ↗
            </a>
            {HAS_CLERK ? <UserButton afterSignOutUrl="/admin/login" /> : null}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <StatBox label="All" count={stats.all} active={statusFilter === "all"} onClick={() => setStatusFilter("all")} />
          <StatBox label="Published" count={stats.published || 0} active={statusFilter === "published"} onClick={() => setStatusFilter("published")} accent="green" />
          <StatBox label="Pending" count={stats.pending || 0} active={statusFilter === "pending"} onClick={() => setStatusFilter("pending")} accent="amber" />
          <StatBox label="Hidden" count={stats.hidden || 0} active={statusFilter === "hidden"} onClick={() => setStatusFilter("hidden")} accent="flame" />
        </div>

        <div className="card p-4 mb-6 flex flex-wrap gap-3 items-center">
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, company, text…" className="flex-1 min-w-[260px] bg-transparent border border-white/15 rounded-lg px-3 py-2 focus:outline-none focus:border-flame" />
          <select value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} className="bg-ink border border-white/15 rounded-lg px-3 py-2 text-sm">
            <option value="">All tags</option>
            {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-ink border border-white/15 rounded-lg px-3 py-2 text-sm">
            <option value="newest">Newest first</option>
            <option value="rating">Highest rated</option>
            <option value="name">By name</option>
          </select>
          <span className="ml-auto text-sm text-white/50">{filtered.length} of {reviews.length}</span>
        </div>

        {loading ? (
          <p className="text-white/50">Loading…</p>
        ) : err ? (
          <p className="text-red-400">{err}</p>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto max-h-[70vh]">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Status</th><th>Rating</th><th>Reviewer</th><th>Review</th><th>Tags</th><th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id}>
                      <td><span className={`status-pill status-${r.status}`}>{r.status}</span></td>
                      <td className="text-flame whitespace-nowrap">{r.rating ? "★".repeat(r.rating) : "—"}</td>
                      <td>
                        <div className="font-medium">{r.customer?.name || "Anonymous"}</div>
                        <div className="text-white/50 text-xs">
                          {r.customer?.title}{r.customer?.title && r.customer?.company ? " · " : ""}{r.customer?.company}
                        </div>
                        {r.customer?.email ? (
                          <div className="text-white/30 text-xs">{r.customer.email}</div>
                        ) : null}
                      </td>
                      <td className="max-w-md">
                        {r.title ? <div className="font-medium mb-1">{r.title}</div> : null}
                        <div className="text-white/70 line-clamp-3">{r.text}</div>
                      </td>
                      <td className="text-xs text-white/60">
                        {(r.tags || []).map((t) => (
                          <span key={t} className="inline-block mr-1 mb-1 px-1.5 py-0.5 rounded bg-white/5 border border-white/10">{t}</span>
                        ))}
                      </td>
                      <td className="text-xs text-white/50 whitespace-nowrap">
                        {r.dates?.review ? new Date(r.dates.review).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-6 text-sm text-white/50">
          <p>
            <strong>Approving / hiding / editing reviews:</strong> click{" "}
            <a href="https://app.senja.io/" className="underline hover:text-flame" target="_blank" rel="noopener">Open Senja ↗</a>{" "}
            — Senja stays the source of truth. Changes flow back here on the next sync (every 6 hours, or trigger manually with <code className="text-flame">npm run sync</code>).
          </p>
        </div>
      </main>
    </div>
  );
}

function StatBox({ label, count, active, onClick, accent }) {
  const accentClass = accent === "green" ? "border-green-500/30 text-green-400"
    : accent === "amber" ? "border-yellow-500/30 text-yellow-400"
    : accent === "flame" ? "border-flame/40 text-flame"
    : "border-white/15";
  return (
    <button
      onClick={onClick}
      className={`stat-block ${active ? "ring-2 ring-flame" : ""} ${accentClass} hover:border-white/35 transition cursor-pointer`}
    >
      <div className="stat-num">{count}</div>
      <div className="stat-label">{label}</div>
    </button>
  );
}
